/**
 * Marker <-> world-point resolution (Faz 6.2, OCS attachment semantics).
 *
 * A ParametricRef { entityId, marker } resolves to a world point per the
 * OCS GsMarker convention (see types.ts): line endpoints 0/1, center -3,
 * midpoint -2, polyline vertex/segment spaces, yap's -7 radius handle.
 *
 * "Writable" markers are solver particles (their resolution is written back
 * into the entity data bag); other markers resolve read-only (derived).
 */

import type { Entity } from '../../state/store';
import type { Pt3 } from '../commands/geometryMath';
import {
    MARKER_LINE_START,
    MARKER_LINE_END,
    MARKER_CENTER,
    MARKER_MIDPOINT,
    MARKER_RADIUS,
    POLYLINE_SEGMENT_BASE,
    POLYLINE_MIDPOINT_BASE,
    POLYLINE_CENTER_BASE,
} from './types';

const asPt = (p: number[] | Pt3 | undefined): Pt3 | null =>
    Array.isArray(p) && p.length >= 2 ? [p[0], p[1], p[2] ?? 0] : null;

/** Polyline vertex list honoring the closed flag (open view of the ring). */
function polylineVertices(d: Record<string, any>): Pt3[] {
    const raw = Array.isArray(d.vertices) ? (d.vertices as Pt3[]) : [];
    return raw.map((v) => [v[0], v[1], v[2] ?? 0] as Pt3);
}

/** Segment endpoints of a polyline segment index (closed-aware). */
function polylineSegment(d: Record<string, any>, index: number): [Pt3, Pt3] | null {
    const vs = polylineVertices(d);
    const closed = !!d.closed;
    const segs = closed ? vs.length : vs.length - 1;
    if (index < 0 || index >= segs) return null;
    const a = vs[index];
    const b = vs[(index + 1) % vs.length];
    return [a, b];
}

/**
 * Resolve one marker to its current world point. Read path for glyphs,
 * measurement and pick matching — never mutates.
 */
export function resolveMarkerPoint(entity: Entity, marker: number): Pt3 | null {
    if (!entity) return null;
    const d = entity.data as Record<string, any>;
    switch (entity.type) {
        case 'line': {
            if (marker === MARKER_LINE_START) return asPt(d.start);
            if (marker === MARKER_LINE_END) return asPt(d.end);
            if (marker === MARKER_MIDPOINT) {
                const a = asPt(d.start), b = asPt(d.end);
                return a && b ? [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2, a[2]] : null;
            }
            return null;
        }
        case 'rectangle': {
            const c1 = asPt(d.corner1), c2 = asPt(d.corner2);
            if (!c1 || !c2) return null;
            const ring = [c1, [c2[0], c1[1], c1[2]] as Pt3, c2, [c1[0], c2[1], c1[2]] as Pt3];
            if (marker === MARKER_CENTER || marker === MARKER_MIDPOINT) {
                return [(c1[0] + c2[0]) / 2, (c1[1] + c2[1]) / 2, c1[2]];
            }
            if (marker >= 0 && marker < 4) return ring[marker];
            return null;
        }
        case 'polyline':
        case 'polygon': {
            const vs = polylineVertices(d);
            if (marker >= 0 && marker < vs.length) return vs[marker];
            if (marker <= POLYLINE_SEGMENT_BASE) {
                const idx = POLYLINE_SEGMENT_BASE - marker;
                const seg = polylineSegment(d, idx);
                return seg ? seg[0] : null;
            }
            if (marker <= POLYLINE_MIDPOINT_BASE && marker > POLYLINE_CENTER_BASE) {
                const idx = POLYLINE_MIDPOINT_BASE - marker;
                const seg = polylineSegment(d, idx);
                return seg
                    ? [(seg[0][0] + seg[1][0]) / 2, (seg[0][1] + seg[1][1]) / 2, seg[0][2]]
                    : null;
            }
            if (marker <= POLYLINE_CENTER_BASE && marker > POLYLINE_CENTER_BASE - 1_000_000) {
                // Arc (bulge) segment center; straight segments fall back to
                // the chord midpoint (no true arc to bisect).
                const idx = POLYLINE_CENTER_BASE - marker;
                const seg = polylineSegment(d, idx);
                return seg
                    ? [(seg[0][0] + seg[1][0]) / 2, (seg[0][1] + seg[1][1]) / 2, seg[0][2]]
                    : null;
            }
            if (marker === MARKER_CENTER || marker === MARKER_MIDPOINT) {
                // Bounded-curve midpoint approximation: vertex centroid.
                if (vs.length === 0) return null;
                const sx = vs.reduce((s, p) => s + p[0], 0) / vs.length;
                const sy = vs.reduce((s, p) => s + p[1], 0) / vs.length;
                return [sx, sy, vs[0][2]];
            }
            return null;
        }
        case 'circle':
        case 'ellipse': {
            const c = asPt(d.center);
            if (!c) return null;
            if (marker === MARKER_CENTER) return c;
            if (marker === MARKER_RADIUS && entity.type === 'circle') {
                return [c[0] + (d.radius as number), c[1], c[2]];
            }
            if (marker === MARKER_MIDPOINT) return c;
            if (marker === -4 || marker === -5) return c; // ellipse axis handles resolve at center
            return null;
        }
        case 'arc': {
            const c = asPt(d.center);
            if (!c) return null;
            const r = typeof d.radius === 'number' ? d.radius : 0;
            if (marker === MARKER_CENTER) return c;
            if (marker === MARKER_RADIUS) return [c[0] + r, c[1], c[2]];
            if (marker === MARKER_LINE_START) {
                return [c[0] + Math.cos(d.startAngle) * r, c[1] + Math.sin(d.startAngle) * r, c[2]];
            }
            if (marker === MARKER_LINE_END) {
                return [c[0] + Math.cos(d.endAngle) * r, c[1] + Math.sin(d.endAngle) * r, c[2]];
            }
            if (marker === MARKER_MIDPOINT) {
                const mid = (d.startAngle + d.endAngle) / 2;
                return [c[0] + Math.cos(mid) * r, c[1] + Math.sin(mid) * r, c[2]];
            }
            return null;
        }
        case 'curve': {
            // Legacy control-point curve: markers index control points.
            const cps = Array.isArray(d.controlPoints) ? (d.controlPoints as Pt3[]) : [];
            if (marker >= 0 && marker < cps.length) return [cps[marker][0], cps[marker][1], cps[marker][2] ?? 0];
            if ((marker === MARKER_CENTER || marker === MARKER_MIDPOINT) && cps.length > 0) {
                const sx = cps.reduce((s, p) => s + p[0], 0) / cps.length;
                const sy = cps.reduce((s, p) => s + p[1], 0) / cps.length;
                return [sx, sy, cps[0][2] ?? 0];
            }
            return null;
        }
        default:
            return null;
    }
}

/**
 * Markers that act as solver particles for one entity (writable, in a
 * stable order). Whole-entity Fixed/translate constraints pin all of them.
 */
export function particleMarkers(entity: Entity): number[] {
    if (!entity) return [];
    const d = entity.data as Record<string, any>;
    switch (entity.type) {
        case 'line':
            return [0, 1];
        case 'rectangle':
            return [0, 2];
        case 'polyline':
        case 'polygon': {
            const n = polylineVertices(d).length;
            return Array.from({ length: Math.max(0, n) }, (_, i) => i);
        }
        case 'circle':
        case 'ellipse':
            return entity.type === 'circle' ? [MARKER_CENTER, MARKER_RADIUS] : [MARKER_CENTER];
        case 'arc':
            return [MARKER_CENTER, MARKER_RADIUS];
        case 'curve': {
            const cps = Array.isArray(d.controlPoints) ? (d.controlPoints as Pt3[]) : [];
            return Array.from({ length: Math.max(0, cps.length) }, (_, i) => i);
        }
        default:
            return [];
    }
}

/** True when the marker's world point can be written back (a particle). */
export function isWritableMarker(entity: Entity, marker: number): boolean {
    return particleMarkers(entity).includes(marker);
}

/**
 * Write solved particle positions into a cloned data bag. Returns the next
 * data bag, or null when nothing applies. Central point updates land first
 * so radius writes measure against the fresh center.
 */
export function applyMarkerPoints(
    entity: Entity,
    points: Map<number, Pt3>
): Record<string, any> | null {
    if (!entity || points.size === 0) return null;
    const d = entity.data as Record<string, any>;
    const next: Record<string, any> = { ...d };
    const get = (m: number): Pt3 | undefined => points.get(m);

    switch (entity.type) {
        case 'line': {
            const s = get(0), e = get(1);
            if (s) next.start = [...s];
            if (e) next.end = [...e];
            return s || e ? next : null;
        }
        case 'rectangle': {
            const c1 = get(0), c2 = get(2);
            if (c1) next.corner1 = [...c1];
            if (c2) next.corner2 = [...c2];
            return c1 || c2 ? next : null;
        }
        case 'polyline':
        case 'polygon': {
            const vs = (Array.isArray(d.vertices) ? d.vertices : []).map((v: Pt3) => [...v] as Pt3);
            let touched = false;
            points.forEach((p, m) => {
                if (m >= 0 && m < vs.length) { vs[m] = [...p]; touched = true; }
            });
            if (!touched) return null;
            next.vertices = vs;
            return next;
        }
        case 'circle': {
            const c = get(MARKER_CENTER);
            if (c) next.center = [...c];
            const rp = get(MARKER_RADIUS);
            const base = c ?? asPt(d.center);
            if (rp && base) next.radius = Math.max(1e-6, Math.hypot(rp[0] - base[0], rp[1] - base[1]));
            return c || rp ? next : null;
        }
        case 'ellipse': {
            const c = get(MARKER_CENTER);
            if (!c) return null;
            next.center = [...c];
            return next;
        }
        case 'arc': {
            const c = get(MARKER_CENTER);
            const base = asPt(d.center);
            if (c) {
                // Translate the whole arc: sweep angles are preserved.
                next.center = [...c];
                if (base && !points.has(MARKER_RADIUS)) {
                    const r = typeof d.radius === 'number' ? d.radius : 0;
                    next.radiusPoint = [c[0] + r, c[1], c[2] ?? 0];
                }
            }
            const rp = get(MARKER_RADIUS);
            const center = c ?? base;
            if (rp && center) next.radius = Math.max(1e-6, Math.hypot(rp[0] - center[0], rp[1] - center[1]));
            return c || rp ? next : null;
        }
        case 'curve': {
            const cps = (Array.isArray(d.controlPoints) ? d.controlPoints : []).map((v: Pt3) => [...v] as Pt3);
            let touched = false;
            points.forEach((p, m) => {
                if (m >= 0 && m < cps.length) { cps[m] = [...p]; touched = true; }
            });
            if (!touched) return null;
            next.controlPoints = cps;
            return next;
        }
        default:
            return null;
    }
}

/**
 * Match a picked world point to the nearest writable marker of the entity
 * (the command-layer equivalent of picking a grip): used by every GC/DC
 * runner to turn "pick + click location" into a ParametricRef.
 */
export function nearestWritableMarker(
    entity: Entity,
    world: [number, number]
): { marker: number; point: Pt3 } | null {
    let best: { marker: number; point: Pt3 } | null = null;
    let bestDist = Infinity;
    for (const marker of particleMarkers(entity)) {
        const p = resolveMarkerPoint(entity, marker);
        if (!p) continue;
        const dist = Math.hypot(p[0] - world[0], p[1] - world[1]);
        if (dist < bestDist) {
            bestDist = dist;
            best = { marker, point: p };
        }
    }
    return best;
}

/**
 * Match a picked point to any resolvable marker (including derived ones) —
 * used when the constraint references a sub-point it cannot drive.
 */
export function nearestResolvableMarker(
    entity: Entity,
    world: [number, number],
    candidates?: number[]
): { marker: number; point: Pt3 } | null {
    const list = candidates ?? [0, 1, MARKER_MIDPOINT, MARKER_CENTER];
    let best: { marker: number; point: Pt3 } | null = null;
    let bestDist = Infinity;
    for (const marker of list) {
        const p = resolveMarkerPoint(entity, marker);
        if (!p) continue;
        const dist = Math.hypot(p[0] - world[0], p[1] - world[1]);
        if (dist < bestDist) {
            bestDist = dist;
            best = { marker, point: p };
        }
    }
    return best;
}

/**
 * Segment index of the polyline segment nearest to a picked point (the
 * PointOnCurve/Midpoint pick semantics: the clicked stroke wins).
 */
export function nearestPolylineSegment(entity: Entity, world: [number, number]): number | null {
    if (!entity || (entity.type !== 'polyline' && entity.type !== 'polygon')) return null;
    const d = entity.data as Record<string, any>;
    const vs = polylineVertices(d);
    const segs = d.closed ? vs.length : vs.length - 1;
    let best: number | null = null;
    let bestDist = Infinity;
    for (let i = 0; i < segs; i++) {
        const seg = polylineSegment(d, i);
        if (!seg) continue;
        // Distance to the segment (point-to-segment projection).
        const [a, b] = seg;
        const abx = b[0] - a[0], aby = b[1] - a[1];
        const len2 = abx * abx + aby * aby || 1;
        const t = Math.max(0, Math.min(1, ((world[0] - a[0]) * abx + (world[1] - a[1]) * aby) / len2));
        const dist = Math.hypot(world[0] - (a[0] + t * abx), world[1] - (a[1] + t * aby));
        if (dist < bestDist) { bestDist = dist; best = i; }
    }
    return best;
}

/** Human-readable ref label for reports/tooltips (OCS ref formatting). */
export function formatRef(entity: Entity | undefined, marker: number): string {
    const name = entity?.name ?? 'deleted';
    if (marker === MARKER_CENTER) return `${name}.center`;
    if (marker === MARKER_RADIUS) return `${name}.radius`;
    if (marker === MARKER_MIDPOINT) return `${name}.midpoint`;
    if (marker <= POLYLINE_MIDPOINT_BASE && marker > POLYLINE_CENTER_BASE) {
        return `${name}.seg${POLYLINE_MIDPOINT_BASE - marker}.mid`;
    }
    if (marker <= POLYLINE_SEGMENT_BASE) return `${name}.seg${POLYLINE_SEGMENT_BASE - marker}`;
    return `${name}.p${marker}`;
}
